// Pure, dependency-free naming-convention helpers (parsing, bucketing, filter matching).
// Lives in plain .js (no @/ aliases, no JSX) so it is unit-testable via `node --test`.

export const NAMING_CONVENTION_GROUP_KEY = 'naming_convention'
export const UNMATCHED_KEY = '__unmatched__'
export const UNMATCHED_LABEL = 'Unmatched'


export const getNamingConventionProperty = (namingConvention, propertyName) => {
    if (!propertyName || !Array.isArray(namingConvention?.properties)) {
        return null
    }
    return namingConvention.properties.find(property => property.name === propertyName) || null
}


const getMaxPosition = (namingConvention) => {
    const positions = (namingConvention?.properties || []).map(property => Number(property.position) || 0)
    return positions.length ? Math.max(...positions) : 0
}


// Raw value of one property inside an ad name, or null. Not trimmed: spaces may be part of the value.
export const extractNamingConventionValue = (adName, namingConvention, property) => {
    if (!adName || typeof adName !== 'string' || !property) {
        return null
    }

    const separator = namingConvention?.separator || '_'
    const mode = namingConvention?.mode || 'identifier'

    if (mode === 'position') {
        const position = property.position
        if (position === undefined || position === null || position < 1) {
            return null
        }
        const parts = adName.split(separator)
        // A name without the separator doesn't follow a multi-part convention; without
        // this guard its whole name would read as the value of position 1.
        if (parts.length === 1 && getMaxPosition(namingConvention) > 1) {
            return null
        }
        return parts[position - 1] || null
    }

    const identifier = property.identifier || ''
    const closer = property.closer || ''
    if (!identifier) {
        return null
    }

    const identifierIndex = adName.indexOf(identifier)
    if (identifierIndex === -1) {
        return null
    }

    const valueStartIndex = identifierIndex + identifier.length
    let valueEndIndex
    if (closer) {
        const closerIndex = adName.indexOf(closer, valueStartIndex)
        if (closerIndex === -1) {
            return null
        }
        valueEndIndex = closerIndex
    } else {
        const nextSeparatorIndex = adName.indexOf(separator, valueStartIndex)
        valueEndIndex = nextSeparatorIndex === -1 ? adName.length : nextSeparatorIndex
    }

    return adName.substring(valueStartIndex, valueEndIndex) || null
}


// Buckets ads by a property's value (case-insensitive, trimmed). Ads without a value go to Unmatched, always last.
export const bucketAdsByNamingConvention = (ads, namingConvention, propertyName) => {
    const property = getNamingConventionProperty(namingConvention, propertyName)
    if (!property || !Array.isArray(ads)) {
        return []
    }

    const bucketsByKey = new Map()
    const unmatchedAds = []

    ads.forEach(ad => {
        const rawValue = extractNamingConventionValue(ad?.name, namingConvention, property)
        const label = rawValue ? rawValue.trim() : ''
        if (!label) {
            unmatchedAds.push(ad)
            return
        }
        const key = label.toLowerCase()
        if (!bucketsByKey.has(key)) {
            bucketsByKey.set(key, { key, label, ads: [] })
        }
        bucketsByKey.get(key).ads.push(ad)
    })

    const buckets = [...bucketsByKey.values()].sort((a, b) => a.label.localeCompare(b.label))
    if (unmatchedAds.length) {
        buckets.push({ key: UNMATCHED_KEY, label: UNMATCHED_LABEL, ads: unmatchedAds })
    }
    return buckets
}


// Report cache slot for a group-by. Naming convention needs one slot per property.
export const groupByCacheKey = (groupBy) => {
    if (groupBy?.key === NAMING_CONVENTION_GROUP_KEY) {
        return `${NAMING_CONVENTION_GROUP_KEY}:${groupBy.property}`
    }
    return groupBy?.key
}


export const matchesNamingConventionFilter = (adName, filter) => {
    if (!filter?.chosenNamingConventionProperty || !filter?.namingConventionConfig) {
        return false
    }

    const namingConvention = filter.namingConventionConfig
    const property = getNamingConventionProperty(namingConvention, filter.chosenNamingConventionProperty.name)
    const value = extractNamingConventionValue(adName, namingConvention, property)

    if (filter.operatorValue === 'has_no_value') {
        return !value || !value.trim()
    }

    if (!adName) {
        return false
    }

    if (value === null) {
        return filter.operatorValue === 'is_not'
    }

    const normalizedValue = value.toLowerCase().trim()
    const filterValues = (Array.isArray(filter.value) ? filter.value : []).map(v => String(v).toLowerCase().trim())
    const isIncluded = filterValues.includes(normalizedValue)

    if (filter.operatorValue === 'is') {
        return isIncluded
    }
    if (filter.operatorValue === 'is_not') {
        return !isIncluded
    }
    return false
}


// One custom group per value (case-insensitive dedupe) plus an Unmatched group.
// Filter shape matches what FiltersDialog produces for a naming_convention filter.
export const buildNamingConventionCustomGroups = (namingConvention, propertyName, values) => {
    const property = getNamingConventionProperty(namingConvention, propertyName)
    if (!property) {
        return []
    }

    const baseFilter = {
        optionId: 'naming_convention',
        label: 'Naming convention',
        chosenNamingConventionProperty: property,
        namingConventionSeparator: namingConvention.separator || '_',
        namingConventionConfig: namingConvention,
    }

    const seenKeys = new Set()
    const groups = []
    const valueList = values || []
    valueList.forEach(value => {
        const label = String(value ?? '').trim()
        const key = label.toLowerCase()
        if (!label || seenKeys.has(key)) {
            return
        }
        seenKeys.add(key)
        groups.push({
            id: `${propertyName}:${key}`,
            name: label,
            groupFilters: [[{ ...baseFilter, operatorValue: 'is', value: [label] }]],
        })
    })

    if (!groups.length) {
        return []
    }

    // One inner OR array: anything not in the generated labels, or with no value at all.
    const generatedLabels = groups.map(group => group.name)
    groups.push({
        id: `${propertyName}:${UNMATCHED_KEY}`,
        name: UNMATCHED_LABEL,
        groupFilters: [[
            { ...baseFilter, operatorValue: 'is_not', value: generatedLabels },
            { ...baseFilter, operatorValue: 'has_no_value', value: [] },
        ]],
    })
    return groups
}
